import Grid from '@mui/material/Grid'
import { useElplanInfo } from 'api/elplan/elplan.api'
import { useOptJobReview } from 'api/optimizations/optimizations.api'
import { useHasPermission } from 'helpers/global.helper/global.helper'
import { DEFAULT_OPTIMIZATION_PERIOD_HOURS } from 'helpers/optimize.helper/optimize.helper'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import globalStore from 'store/global/global'
import LoadingPlaceholderContainer from 'ui/atoms/LoadingPlaceholderContainer/LoadingPlaceholderContainer'
import { getTabFromStorage } from 'ui/atoms/Tabs/getTabFromStorage'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import { useChannel } from 'ui/components/PusherContext/PusherContext'
import View from 'ui/components/View/View'
import UiConfig from 'ui/uiConfig/UiConfig'
import Datetime from 'utils/datetime/datetime'
import { useSnapshot } from 'valtio'
import { BidInputCard } from './components/BidInput/BidInputCard'
import IntradayOverview from './components/IntradayOverview/IntradayOverview'
import { IntradayTradingHeader } from './components/IntradayTradingHeader'
import {
  buildIntradayResultAliases,
  getIntradayPricesFromReview,
  isIntradayOptJobActive,
  useLatestIntradayOptJobData,
} from './IntradayTradingView.helpers'
import styles from './IntradayTradingView.module.less'
import { useDualHeaderPopovers } from './utils/hooks/useDualHeaderPopovers'
import { useIntradayBidSubmission } from './utils/hooks/useIntradayBidSubmission'
import { INTRADAY_PLAN_OPT_JOB_TYPE, useIntradayOptimization } from './utils/hooks/useIntradayOptimization'
import { useIntradayUiConfigAvailability } from './utils/hooks/useIntradayUiConfigAvailability'
import { usePriceInputRows } from './utils/hooks/usePriceInputRows'

enum TAB_ID {
  OPTIMIZE = 'id_optimize',
  OVERVIEW = 'id_overview',
}

const INTRADAY_TABS_STORAGE_KEY = 'IntradayTradingTabs'
// Index order must match the `tabs` array rendered into <Tabs> so the stored index maps to the right tab.
const INTRADAY_TAB_ORDER = [TAB_ID.OPTIMIZE, TAB_ID.OVERVIEW] as const

function getInitialIntradayTabId(): TAB_ID {
  const savedIndex = getTabFromStorage(`activeTab_${INTRADAY_TABS_STORAGE_KEY}`, 0)
  return INTRADAY_TAB_ORDER[savedIndex] ?? TAB_ID.OPTIMIZE
}

export function IntradayTradingView(): ReactElement {
  const { t } = useTranslation()
  const width = useSnapshot(globalStore).cardWidth
  const { activeSystem, hasAccess } = useAuth()
  const [activeTab, setActiveTab] = useState<string>(getInitialIntradayTabId)
  const isOptimizeTab = activeTab === TAB_ID.OPTIMIZE
  const tabs = useMemo(
    () => [
      { id: TAB_ID.OPTIMIZE, title: t('Optimize'), children: [] },
      { id: TAB_ID.OVERVIEW, title: t('Overview'), children: [] },
    ],
    [t]
  )
  const { data: elplanInfo, isLoading: isElplanInfoLoading, error: elplanInfoError } = useElplanInfo(activeSystem?.id)
  const hasElplanInfoError = Boolean(elplanInfoError)
  const hasIntradayTradingAccess = useHasPermission('view_intraday_trading')
  const {
    isOptimizeConfigured,
    isOverviewConfigured,
    isLoading: isUiConfigAvailabilityLoading,
  } = useIntradayUiConfigAvailability(hasIntradayTradingAccess ? activeSystem?.id : null)
  const showTabs = isOptimizeConfigured && isOverviewConfigured
  const isOverviewOnly = isOverviewConfigured && !isOptimizeConfigured
  const isOptimizeView = showTabs ? isOptimizeTab : !isOverviewOnly
  const showOptimizeControls = hasIntradayTradingAccess && isOptimizeView && !isUiConfigAvailabilityLoading
  const subtypes = useMemo(() => activeSystem?.subtypes?.['intraday_plan'] ?? [], [activeSystem])
  const [selectedSubtype, setSelectedSubtype] = useState('')
  const activeSubtype = selectedSubtype !== '' ? selectedSubtype : (subtypes[0]?.subtype ?? '')
  const activeSubtypeDisplayName = useMemo(
    () => subtypes.find((subtype) => subtype.subtype === activeSubtype)?.subtype_display_name ?? null,
    [activeSubtype, subtypes]
  )
  const intradayResultAlias = useMemo<UiConfigAliases>(() => {
    return buildIntradayResultAliases({
      subtypes,
      activeSubtype,
      activeSubtypeDisplayName,
    })
  }, [activeSubtype, activeSubtypeDisplayName, subtypes])
  const showSubtypeSwitch = hasAccess({ submodule: 'optimize_subnet', module: 'optimize' }) && subtypes.length > 1
  const showTradeResultSync = hasAccess({
    module: 'intradaytrading',
    submodule: 'intradaytrading_trade_result_sync',
  })
  const showBotStrategySetting = hasAccess({
    module: 'intradaytrading',
    submodule: 'intradaytrading_bot_strategy_setting',
  })
  const tradeResultSyncConfig = showTradeResultSync ? elplanInfo?.intraday_configuration?.trade_result_sync : undefined
  const botStrategyConfig = showBotStrategySetting ? elplanInfo?.intraday_configuration?.bot_strategy : undefined

  const {
    inputPrices,
    resetPriceInputs,
    hasInvalidPriceInputs,
    priceHourLabels,
    handlePriceInputChange,
    handleAddPriceRow,
    handleRemoveLastPriceRow,
  } = usePriceInputRows()

  const {
    id: latestIntradayOptJobId,
    status: latestIntradayOptJobStatus,
    startedAt: latestIntradayOptJobStartedAt,
  } = useLatestIntradayOptJobData(INTRADAY_PLAN_OPT_JOB_TYPE, undefined, activeSubtype ? [activeSubtype] : undefined)
  const {
    id: latestOptJobId,
    timeSince: lastOptimizedAgo,
    isLoading: isLastOptimizationLoading,
    isError: hasLastOptimizationError,
  } = useLatestIntradayOptJobData(INTRADAY_PLAN_OPT_JOB_TYPE, 'Finished', activeSubtype ? [activeSubtype] : undefined)
  const { data: completedOptJobReview } = useOptJobReview({ optJobId: latestOptJobId ?? undefined })
  const { channel: optimizationsChannel } = useChannel(
    activeSystem ? `aurora-django-optimizations-${activeSystem.name}` : undefined
  )

  const {
    datasetRefreshToken,
    showOptimizationIsRunning,
    canRunOptimization,
    handleRunTestOptimization,
    resetOptimizationTracking,
  } = useIntradayOptimization({
    systemId: activeSystem?.id,
    activeSubtype,
    inputPrices,
    resetPriceInputs,
    optimizationsChannel,
    latestIntradayOptJobId,
    latestIntradayOptJobStatus,
    optimizationPeriodHours: activeSystem?.default_optimization_period ?? DEFAULT_OPTIMIZATION_PERIOD_HOURS,
  })

  const popovers = useDualHeaderPopovers()

  // Clear in-flight state when the selected subtype is no longer available (e.g. system change).
  useEffect(() => {
    if (selectedSubtype !== '' && !subtypes.some((s) => s.subtype === selectedSubtype)) {
      setSelectedSubtype('')
      resetOptimizationTracking()
    }
  }, [subtypes, selectedSubtype, resetOptimizationTracking])

  const intradayTradingView = useMemo(
    () => (
      <UiConfig
        type="intraday_trading_view"
        datasetRefreshToken={datasetRefreshToken}
        datasetStartTime={Datetime.getISONow()}
        datasetEndTime={Datetime.getISONow(activeSystem?.default_optimization_period ?? 0)}
        overrideAlias={intradayResultAlias}
        fallback={
          <div className={styles.IntradayTradingView_AnchorEmptyState}>
            <strong>{t('Result panel is not configured')}</strong>
            <span>
              {t(
                'No intraday trading UI config anchor is published for this system yet. Publish the intraday_trading_view anchor to display results here.'
              )}
            </span>
          </div>
        }
      />
    ),
    [datasetRefreshToken, intradayResultAlias, t, activeSystem?.default_optimization_period]
  )

  const reviewPrices = useMemo(() => getIntradayPricesFromReview(completedOptJobReview), [completedOptJobReview])
  const hasCompletedOptimization = latestOptJobId !== null

  const {
    configuredPositions,
    hasIntradayConfig,
    isSubmitting: isSubmittingPositions,
    handleSubmitPositions,
  } = useIntradayBidSubmission({ systemId: activeSystem?.id, elplanInfo })

  function handleSubtypeChange(subtype: string): void {
    setSelectedSubtype(subtype)
    resetOptimizationTracking()
  }

  const optimizationRunningMessage = isIntradayOptJobActive(latestIntradayOptJobStatus)
    ? latestIntradayOptJobStartedAt
      ? t('New optimization is running, started at {{time}}', { time: latestIntradayOptJobStartedAt })
      : t('New optimization is running')
    : null

  const optimizationStatusText = useMemo(() => {
    if (hasLastOptimizationError) {
      return t('Last optimization time is currently unavailable')
    }

    if (isLastOptimizationLoading) {
      return t('Loading latest optimization status...')
    }

    if (lastOptimizedAgo !== null) {
      return t('Last optimized: {{time}} ago', { time: lastOptimizedAgo })
    }

    return t('No completed optimization yet')
  }, [hasLastOptimizationError, isLastOptimizationLoading, lastOptimizedAgo, t])

  const optimizeButtonLabel = useMemo(() => {
    if (showOptimizationIsRunning) {
      return t('Optimizing...')
    }

    return activeSubtypeDisplayName ? `${t('Optimize')} ${activeSubtypeDisplayName}` : t('Optimize')
  }, [activeSubtypeDisplayName, showOptimizationIsRunning, t])

  const optimizeButtonDisabled = !canRunOptimization || hasInvalidPriceInputs || showOptimizationIsRunning
  const optimizeButtonTooltip = showOptimizationIsRunning
    ? (optimizationRunningMessage ?? '')
    : hasInvalidPriceInputs
      ? t('Please correct invalid price values before optimizing')
      : !canRunOptimization
        ? t('Please enter at least one input price')
        : ''

  return (
    <div className={styles.IntradayTradingView}>
      <View
        stickyHeader
        header={
          <IntradayTradingHeader
            showOptimizeControls={showOptimizeControls}
            showSubtypeSwitch={showSubtypeSwitch}
            subtypes={subtypes}
            activeSubtype={activeSubtype}
            onSubtypeChange={handleSubtypeChange}
            popovers={popovers}
            reviewPrices={reviewPrices}
            hasCompletedOptimization={hasCompletedOptimization}
            inputPrices={inputPrices}
            priceHourLabels={priceHourLabels}
            hasInvalidPriceInputs={hasInvalidPriceInputs}
            onPriceChange={handlePriceInputChange}
            onRemoveLastRow={handleRemoveLastPriceRow}
            onAddRow={handleAddPriceRow}
            optimizeButtonLabel={optimizeButtonLabel}
            optimizeButtonDisabled={optimizeButtonDisabled}
            optimizeButtonTooltip={optimizeButtonTooltip}
            optimizationStatusText={optimizationStatusText}
            showOptimizationIsRunning={showOptimizationIsRunning}
            onOptimize={handleRunTestOptimization}
            showTabs={showTabs}
            tabs={tabs}
            tabsStorageKey={INTRADAY_TABS_STORAGE_KEY}
            onTabChange={setActiveTab}
          />
        }
      >
        {!hasIntradayTradingAccess ? (
          <Grid width={width} className={styles.IntradayTradingView_Content}>
            <div>{t('Unfortunately, you do not have permission to view this page')}</div>
          </Grid>
        ) : isUiConfigAvailabilityLoading ? (
          <Grid width={width} className={styles.IntradayTradingView_Content}>
            <LoadingPlaceholderContainer />
          </Grid>
        ) : isOptimizeView ? (
          <Grid width={width} className={styles.IntradayTradingView_Content}>
            {intradayTradingView}

            <BidInputCard
              configuredPositions={configuredPositions}
              isConfigLoading={isElplanInfoLoading}
              hasConfigError={hasElplanInfoError}
              hasIntradayConfig={hasIntradayConfig}
              isSubmitting={isSubmittingPositions}
              onSubmit={handleSubmitPositions}
              activeSubtype={activeSubtype}
              systemId={activeSystem?.id}
              tradeResultSyncConfig={tradeResultSyncConfig}
              showBotStrategySetting={showBotStrategySetting}
              botStrategyConfig={botStrategyConfig}
            />
          </Grid>
        ) : (
          <Grid size={{ xs: 12 }} width={width}>
            <IntradayOverview />
          </Grid>
        )}
      </View>
    </div>
  )
}
